/**
 * UNIWERSALNY SILNIK SKÓREK — DEKLARATYWNY GRAF SCENY (OWNER 2026-10-08).
 *
 * <b>Po co.</b> Silnik 1 i 2 znały z góry RODZAJE ozdób (scena nagłówka,
 * akcent przy logo, tło nagłówka, bliźniak statyczny…). Każdy nowy pomysł
 * graficzny, który się w nie nie mieścił, wymagał nowego miejsca w kodzie —
 * czyli wydania aplikacji w sklepach. Ten model odwraca kierunek: skórka nie
 * mówi „uruchom skórkę Ninja", tylko „narysuj TE warstwy w TYCH regionach".
 * Aplikacja zna wyłącznie prymitywy (obraz, tekst, kształt, pole cząstek),
 * regiony ekranu i bibliotekę ogólnych efektów ruchu. Wszystko inne to DANE.
 *
 * <b>Granica bezpieczeństwa.</b> Żadnego zdalnego HTML, JS ani CSS. Każda
 * właściwość jest z listy dozwolonych i trafia do stylu jako liczba albo
 * wartość z zamkniętego wyliczenia — style buduje aplikacja, nie skórka.
 * Obrazki idą wyłącznie przez `src` (`<img>`: SVG bez skryptów i sieci).
 *
 * <b>Wersjonowanie.</b> `UNIWERSALNY_SILNIK` rośnie TYLKO wtedy, gdy dochodzi
 * prymityw/efekt/region, którego starsza aplikacja nie umie narysować.
 * Rozszerzenia zgodne wstecz (nowe opcjonalne pole) go nie ruszają — starsza
 * aplikacja pole zignoruje. Warstwa z nieznanym typem/efektem może być
 * oznaczona `optional: true`: starsza aplikacja ją pominie zamiast odrzucać
 * cały motyw.
 */

/** Wersja uniwersalnego silnika (`compatibility.minUniversalEngine`). */
export const UNIWERSALNY_SILNIK = 1;

/** Wersja schematu konfiguracji grafu sceny (`schemaVersion`). */
export const WERSJA_SCHEMATU = 3;

/**
 * Regiony ekranu — GENERYCZNE, nie okazjonalne. Region to prostokąt, który
 * aplikacja zna i umie wskazać (nagłówek, pasek, hero…). Skórka nie może
 * przestawiać sekcji aplikacji — dekoruje wyłącznie regiony.
 */
export const REGIONY = [
  /** Cały ciemny nagłówek (z wcięciem paska statusu), POD logo/datą/zegarem. */
  'APP_HEADER',
  /** Prostokąt logo „Tu Szama!" — ozdoby przy logo (nad dymkiem, obok napisu). */
  'LOGO',
  /** Żółty pasek ogłoszeń — ozdoby NAD tłem paska, pod tekstem. */
  'MESSAGE_BAR',
  /** Blok okolicznościowy w PRZEPŁYWIE, pod paskiem, nad „Restauracjami". */
  'EVENT_HERO',
  /** Pas w PRZEPŁYWIE między hero a pierwszą sekcją. */
  'PRE_RESTAURANTS',
  /** Tło treści menu — POD sekcjami (wysokość z konfiguracji albo cała treść). */
  'CONTENT_BACKGROUND',
  /** Nad sekcjami treści — ozdoby, które przykrywają karty (bez dotyku). */
  'CONTENT_FOREGROUND',
  /** Nagłówek KAŻDEJ sekcji (tytuł + „Pokaż wszystkie") — akcenty przy tytułach. */
  'SECTION_HEADER',
  /** Pas w PRZEPŁYWIE po ostatniej sekcji. */
  'POST_CONTENT',
  /** Tło sekcji stopki („Social media") — kafle rysują się nad nim. */
  'FOOTER',
  /** Cały ekran (stały względem okna) — nad treścią, pod nawigacją i oknami. */
  'FULL_SCREEN_OVERLAY',
  /** Puste stany (pusty koszyk, pusta historia). */
  'EMPTY_STATE',
  /** Dolny pasek zakładek — ozdoby POD przyciskami zakładek. */
  'BOTTOM_NAV',
] as const;
export type Region = (typeof REGIONY)[number];

/** Regiony w przepływie strony — mają WŁASNĄ wysokość (z konfiguracji). */
export const REGIONY_W_PRZEPLYWIE: readonly Region[] = ['EVENT_HERO', 'PRE_RESTAURANTS', 'POST_CONTENT'];

/** Kotwice z kontraktu OWNERA → regiony (aliasy dla czytelności konfiguracji). */
export const KOTWICE: Readonly<Record<string, Region>> = {
  SCREEN: 'FULL_SCREEN_OVERLAY',
  HEADER: 'APP_HEADER',
  MESSAGE_BAR: 'MESSAGE_BAR',
  HERO: 'EVENT_HERO',
  CONTENT: 'CONTENT_BACKGROUND',
  RESTAURANTS_SECTION: 'PRE_RESTAURANTS',
  FOOTER: 'FOOTER',
};

export const TYPY_WARSTW = ['IMAGE', 'TEXT', 'SHAPE', 'PARTICLES'] as const;
export type TypWarstwy = (typeof TYPY_WARSTW)[number];

export const TRYBY_POZYCJI = ['ABSOLUTE', 'FIXED', 'FLOW'] as const;
export type TrybPozycji = (typeof TRYBY_POZYCJI)[number];

export const DOPASOWANIA = ['CONTAIN', 'COVER', 'FILL', 'NONE'] as const;
export type Dopasowanie = (typeof DOPASOWANIA)[number];

/** Biblioteka OGÓLNYCH efektów — żaden nie zna znaczenia skórki. */
export const EFEKTY = [
  'FLOAT', 'SWAY', 'BOB', 'PULSE', 'ROTATE', 'SPIN', 'FADE', 'BLINK', 'SHIMMER', 'GLOW',
  'BOUNCE', 'FLY', 'DRIFT', 'FALL', 'RISE', 'ZOOM', 'WIGGLE', 'SHAKE_LIGHT', 'ORBIT',
  'PATH', 'KEYFRAMES', 'PARALLAX', 'SPARKLE',
] as const;
export type RodzajEfektu = (typeof EFEKTY)[number];

export const EASINGI = ['linear', 'ease', 'ease-in', 'ease-out', 'ease-in-out'] as const;
export type Easing = (typeof EASINGI)[number];

export const TRYBY_RUCHU_OGRANICZONEGO = ['FIRST_FRAME', 'STATIC_ASSET', 'HIDE'] as const;
export type RuchOgraniczony = (typeof TRYBY_RUCHU_OGRANICZONEGO)[number];

export const MIESZANIA = ['normal', 'multiply', 'screen', 'overlay', 'soft-light', 'lighten', 'darken'] as const;
export type Mieszanie = (typeof MIESZANIA)[number];

/**
 * Kroje z allowlisty — WYŁĄCZNIE krój marki wbudowany w aplikację (bez zdalnych
 * fontów) i dwa ogólne kroje systemowe (szeryfowy, maszynowy) bez nazw krojów
 * spoza marki. Typografia specjalna = wypalona w grafice (PNG/WebP/SVG).
 */
export const KROJE: Readonly<Record<string, string>> = {
  'Plus Jakarta Sans': "'Plus Jakarta Sans', sans-serif",
  serif: 'Georgia, serif',
  mono: 'ui-monospace, Menlo, Consolas, monospace',
};

/** Długość: liczba + jednostka (px, % regionu, vw, vh). `n` (0..1) → %. */
export interface Dlugosc {
  readonly v: number;
  readonly u: 'px' | '%' | 'vw' | 'vh';
}

export interface KlatkaKluczowa {
  /** Pozycja w cyklu 0..1. */
  readonly t: number;
  readonly x?: number;
  readonly y?: number;
  readonly rotate?: number;
  readonly scale?: number;
  readonly opacity?: number;
}

export interface PunktSciezki {
  readonly t: number;
  /** Znormalizowane 0..1 względem regionu (może wyjść poza: −1..2). */
  readonly x: number;
  readonly y: number;
}

export interface Efekt {
  readonly typ: RodzajEfektu;
  readonly czas: number;          // ms jednego przebiegu
  readonly opoznienie: number;    // ms przed pierwszym przebiegiem
  readonly przerwa: number;       // ms spoczynku po każdym przebiegu (interval)
  readonly powtorzenia: number;   // Infinity = bez końca
  readonly faza: number;          // 0..1 — przesunięcie startu w cyklu
  readonly easing: Easing;
  readonly amplituda: number;     // px (FLOAT/BOB/DRIFT/SHAKE) albo stopnie (SWAY/WIGGLE)
  readonly dystans: number;       // px (FLY/FALL/RISE)
  readonly kierunek: number;      // stopnie: 0 = w prawo, 90 = w dół
  readonly obrot: number;         // stopnie (ROTATE/SPIN/FLY)
  readonly krycie: readonly [number, number];
  readonly skala: readonly [number, number];
  readonly os: readonly [number, number];   // transform-origin 0..1
  readonly naprzemiennie: boolean;
  readonly kolor: string | null;  // GLOW / SHIMMER / SPARKLE
  readonly promien: number;       // GLOW blur / ORBIT promień / SPARKLE rozmiar
  readonly ile: number;           // SPARKLE: liczba błysków
  readonly klatki: readonly KlatkaKluczowa[];
  readonly sciezka: readonly PunktSciezki[];
  readonly obracajWzdluz: boolean; // PATH: obrót zgodnie z kierunkiem lotu
}

export interface Tlo {
  readonly typ: 'SOLID' | 'GRADIENT' | 'IMAGE';
  readonly kolor: string | null;
  readonly kat: number;
  readonly przystanki: readonly { kolor: string; at: number }[];
  readonly url: string | null;
  readonly dopasowanie: 'COVER' | 'CONTAIN' | 'REPEAT' | 'STRETCH';
  readonly pozycja: readonly [number, number];
  readonly krycie: number;
  readonly mieszanie: Mieszanie;
}

export interface Font {
  readonly rodzina: string;   // wartość CSS z KROJE
  readonly waga: number;
  readonly rozmiar: number;
  readonly interlinia: number;
  readonly odstep: number;
  readonly wersaliki: boolean;
  readonly kursywa: boolean;
}

/** Wspólne pola warstwy (po walidacji — gotowe liczby). */
export interface WarstwaBaza {
  readonly id: string;
  readonly typ: TypWarstwy;
  readonly region: Region;
  readonly tryb: TrybPozycji;
  readonly x: Dlugosc;
  readonly y: Dlugosc;
  readonly dx: number;
  readonly dy: number;
  readonly szer: Dlugosc | null;
  readonly wys: Dlugosc | null;
  readonly maxSzer: number | null;
  readonly maxWys: number | null;
  readonly poczatek: readonly [number, number];
  readonly obrot: number;
  readonly skala: number;
  /** Odbicie lustrzane (np. rybka płynąca w drugą stronę). */
  readonly lustro: 'NONE' | 'X' | 'Y';
  readonly krycie: number;
  readonly z: number;
  readonly efekty: readonly Efekt[];
  readonly ruchOgraniczony: RuchOgraniczony;
  readonly urlStatyczny: string | null;
  /** Odcinki widoczności: ekran węższy/szerszy niż (px). */
  readonly minEkran: number | null;
  readonly maxEkran: number | null;
  /** Warianty responsywne — pierwszy pasujący do szerokości ekranu zastępuje warstwę. */
  readonly warianty?: readonly Warstwa[];
}

export interface WarstwaObraz extends WarstwaBaza {
  readonly typ: 'IMAGE';
  readonly url: string;
  readonly dopasowanie: Dopasowanie;
  /** Plik sam się animuje (SVG z CSS w środku / GIF) — ruch epizodami. */
  readonly animowanyPlik: boolean;
  readonly epizod: readonly [number, number] | null;  // [ms ruchu, ms spoczynku]
}

export interface WarstwaTekst extends WarstwaBaza {
  readonly typ: 'TEXT';
  readonly tekst: string;
  readonly font: Font;
  readonly kolor: string;
  readonly wyrownanie: 'left' | 'center' | 'right';
  readonly maxLinii: number;
  readonly cien: { kolor: string; rozmycie: number } | null;
}

export interface WarstwaKsztalt extends WarstwaBaza {
  readonly typ: 'SHAPE';
  readonly ksztalt: 'RECT' | 'ELLIPSE';
  readonly wypelnienie: Tlo;
  readonly zaokraglenie: number;
  readonly obrys: { kolor: string; grubosc: number } | null;
}

export interface WarstwaCzastki extends WarstwaBaza {
  readonly typ: 'PARTICLES';
  readonly url: string;
  readonly ile: number;
  /** Obszar narodzin — znormalizowany [x, y, w, h] względem regionu. */
  readonly obszar: readonly [number, number, number, number];
  readonly kierunek: number;
  readonly predkosc: readonly [number, number];   // px/s
  readonly droga: number | null;                  // px; null = do końca regionu
  readonly obrotCzastki: readonly [number, number];
  readonly skalaCzastki: readonly [number, number];
  readonly kryciePrzedzial: readonly [number, number];
  readonly zycie: readonly [number, number];      // ms
  readonly opoznienia: readonly [number, number]; // ms
  readonly dryf: number;                          // px
  readonly rozmiar: number;                       // px szerokości cząstki
}

export type Warstwa = WarstwaObraz | WarstwaTekst | WarstwaKsztalt | WarstwaCzastki;

export interface ScenaRegionu {
  readonly region: Region;
  readonly tlo: Tlo | null;
  /** Wysokość regionu: px, `'FULL'` (cała treść) albo `null` (z warstw FLOW / region aplikacji). */
  readonly wysokosc: number | 'FULL' | null;
  /** Szer./wys. — wysokość regionu w przepływie z proporcji. */
  readonly proporcja: number | null;
  readonly maxWysokosc: number | null;
  readonly przytnij: boolean;
  readonly warstwy: readonly Warstwa[];
}

export interface Scena {
  readonly regiony: Readonly<Partial<Record<Region, ScenaRegionu>>>;
  /** Każdy adres, który musi się wczytać PRZED pokazem (wszystko albo nic). */
  readonly adresy: readonly string[];
}

/** LIMITY — chronią aplikację przed konfiguracją, która by ją zadusiła. */
export const LIMITY_SCENY = {
  warstw: 48,
  warstwRegionu: 24,
  efektowWarstwy: 4,
  czastekPola: 40,
  czastekRazem: 120,
  klatek: 24,
  punktowSciezki: 32,
  znakowTekstu: 160,
  zasobow: 32,
  wysokoscRegionu: 2400,
  wysokoscPrzeplywu: 480,
} as const;
